import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, TabBar, TabItem } from '@wot/components';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns/esm';
import Chip from '../../components/Chip';
import { FILTER_SVG } from '../../assets/icons';
import { SPINNER } from '../../assets/animated';
import NoResults from '../../components/NoResults';
import Pagination from '../../components/Pagination';
import usePagainate from '../../hooks/usePaginate';
import { useTab } from '../../hooks/useTab';
import { useClickOutside } from '@wot/shared';
import { TableBody, TableHeading, TableHeadingCol, TableRow, TableRowItemData } from '../../components/Table';
import { BiSort } from 'react-icons/bi';
import ticketsApi, { Ticket as TicketType, TicketFilters as ApiTicketFilters } from '../../api/tickets';

interface Filters {
  priority: 'high' | 'medium' | 'low' | "";
  topic: 'payments' | 'scheduling' | 'technical' | 'general' | 'invoices' | "";
}

export type Sorting = 'createdAt' | 'priority' | 'subject' | 'fullName'

function TicketTableRowItem({ ticket }: { ticket: TicketType }) {
  return (
    <>
      <TableRowItemData text={format(new Date(ticket.createdAt), 'dd/MM/yyyy')} />
      <TableRowItemData text={ticket._id} />
      <TableRowItemData text={ticket.fullName} />
      {/* <TableRowItemData text={ticket.description} /> */}
      <TableRowItemData text={ticket.email} />
      <TableRowItemData text={ticket.phoneNumber} />
      <TableRowItemData text={ticket.subject} />
      <TableRowItemData text={ticket.topic} />
      <TableRowItemData
        component={
          <Chip state={ticket.priority === 'high' ? 'error' : ticket.priority === 'medium' ? 'warning' : 'pending'}>
            <p className="text-sm font-semibold capitalize">{ticket.priority}</p>
          </Chip>
        }
      />
      <TableRowItemData
        component={
          <Chip state={(ticket.status || 'active') === 'active' ? 'warning' : (ticket.status || 'active') === 'resolved' ? 'success' : 'pending'}>
            <p className="text-sm font-semibold capitalize">{ticket.status || 'active'}</p>
          </Chip>
        }
      />
    </>
  );
}

interface TableContainerProps {
  data: TicketType[];
  handleSort: (key: Sorting) => void;
}

function TicketTableContainer({ data, handleSort }: TableContainerProps) {
  const navigate = useNavigate();
  return (
    <table className="w-full h-full table-auto">
      <TableHeading>
        <TableRow>
          <TableHeadingCol
            label="DATE"
            component={
              <div className="flex items-center">
                <p className="">DATE</p>
                <BiSort className="text-sm" />
              </div>
            }
            onClick={() => handleSort('createdAt')}
          />
          <TableHeadingCol label="ID" />
          <TableHeadingCol
            label="NAME"
            component={
              <div className="flex items-center">
                <p className="">NAME</p>
                <BiSort className="text-sm" />
              </div>
            }
            onClick={() => handleSort('fullName')}
          />
          {/* <TableHeadingCol label="Description" /> */}
          <TableHeadingCol label="Email" />
          <TableHeadingCol label="Phone" />
          <TableHeadingCol label="Subject" />
          <TableHeadingCol label="Topic" />
          <TableHeadingCol label="Priority" />
          <TableHeadingCol label="Status" />
        </TableRow>
      </TableHeading>
      <TableBody>
        {data.map((ticket) => (
          <TableRow key={ticket._id} onClick={() => navigate(`/enquiry-management/ticket-raise/${ticket._id}`)}>
            <TicketTableRowItem ticket={ticket} />
          </TableRow>
        ))}
      </TableBody>
    </table>
  );
}

interface FiltersProps {
  onApply: (filters: Filters) => void;
  onClear: () => void;
  filters: Filters;
}

function TicketFilters({ filters, onApply, onClear }: FiltersProps) {
  const [showOptions, setShowOptions] = useState(false);
  const filterSortRef = useRef(null);

  const [temp, setTemp] = useState<Filters>({
    priority: "",
    topic: ""
  });

  useEffect(() => {
    if (Object.keys(filters).length > 0 && 'priority' in filters) {
      setTemp(filters);
    }
  }, [filters]);

  useClickOutside(filterSortRef, () => setShowOptions(false));

  return (
    <div className='relative flex justify-end'>
      <div onClick={() => setShowOptions(true)} className={`w-7 h-7 ${Object.keys(filters).length > 0 ? "bg-blue-100" : "bg-white"} flex items-center justify-center cursor-pointer border border-gray-300 rounded-md p-1 drop-shadow-sm`}>
        <img src={FILTER_SVG} />
      </div>
      {showOptions &&
        <div
          ref={filterSortRef}
          className="md:min-w-[500px] min-w-full flex flex-col border border-neutralBlue1 absolute top-14 md:right-5 right-0 p-6 bg-white drop-shadow-lg rounded-lg z-40"
        >
          <div className="flex flex-col gap-4 pt-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-3">
                <p className="text-sm font-medium">Filter By Priority</p>
                <div
                  className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                  onClick={() => setTemp({ ...temp, priority: "" })}
                >
                  Clear
                </div>
              </div>
              <div className="flex sm:flex-row flex-col gap-4 sm:gap-0 justify-between">
                <div
                  className={`${temp.priority === 'high' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, priority: "high" })}
                >
                  High
                </div>
                <div
                  className={`${temp.priority === 'medium' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, priority: "medium" })}
                >
                  Medium
                </div>
                <div
                  className={`${temp.priority === 'low' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, priority: "low" })}
                >
                  Low
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-3">
                <p className="text-sm font-medium">Filter By Topic</p>
                <div
                  className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                  onClick={() => setTemp({ ...temp, topic: "" })}
                >
                  Clear
                </div>
              </div>
              <div className="flex sm:flex-row flex-col gap-4 sm:gap-0 justify-between">
                <div
                  className={`${temp.topic === 'payments' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[24%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, topic: "payments" })}
                >
                  Payments
                </div>
                <div
                  className={`${temp.topic === 'scheduling' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[24%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, topic: "scheduling" })}
                >
                  Scheduling
                </div>
                <div
                  className={`${temp.topic === 'technical' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[24%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, topic: "technical" })}
                >
                  Technical
                </div>
                <div
                  className={`${temp.topic === 'general' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[19%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, topic: "general" })}
                >
                  General
                </div>
                <div
                  className={`${temp.topic === 'invoices' ? 'bg-neutralBlue2' : 'bg-white'
                    } border border-neutralBlue1 basis-[19%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                  onClick={() => setTemp({ ...temp, topic: "invoices" })}
                >
                  Invoices
                </div>
              </div>
            </div>
          </div>

          <div className="flex mt-5 gap-7">
            <Button
              text="Clear All"
              block
              type="s-outlined"
              onClick={() => {
                onClear();
              }}
            />
            <Button
              text="Apply"
              block
              type="primary"
              onClick={() => {
                onApply(temp);
                setShowOptions(false);
              }}
            />
          </div>
        </div>
      }
    </div >
  );
}

export default function RaiseTicket() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { activeTab, onTabChange } = useTab('active');
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ "createdAt": -1 });
  const [filters, setFilters] = useState<Filters>({ priority: "", topic: "" });

  const handleSort = (key: Sorting) => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  useEffect(() => {
    resetPage();
  }, [activeTab, filters]);

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey: ['tickets', currentPage, resultsPerPage, activeTab, sortBy, filters],
    queryFn: async () => {
      const apiFilters: any = {};

      if (activeTab !== 'active') {
        apiFilters.status = activeTab;
      }
      if (filters.priority) {
        apiFilters.priority = filters.priority;
      }
      if (filters.topic) {
        apiFilters.topic = filters.topic;
      }

      try {
        const result = await ticketsApi.get(currentPage, resultsPerPage, apiFilters, sortBy);
            return result;
      } catch (error) {
        console.error('Tickets API error:', error);
        throw error;
      }
    }
  });

  useEffect(() => {
    if (!isPreviousData && data) {
      queryClient.prefetchQuery({
        queryKey: ['tickets', currentPage + 1, resultsPerPage, activeTab, sortBy, filters],
        queryFn: async () => {
          const apiFilters: any = {};

          if (activeTab !== 'active') {
            apiFilters.status = activeTab;
          }
          if (filters.priority) {
            apiFilters.priority = filters.priority;
          }
          if (filters.topic) {
            apiFilters.topic = filters.topic;
          }

          return {
            count: 0,
            data: []
          };
        }
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, activeTab, sortBy, filters]);

  const filterResults = (filters: Filters) => {
    setFilters(filters);
  };

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <h1 className="text-xl font-semibold">Raise Ticket</h1>
      <TabBar value={activeTab} onChange={onTabChange} textColor="text-black" indicatorColor="bg-primary">
        <TabItem label="Active" value="active" />
        <TabItem label="In Progress" value="in-progress" />
        <TabItem label="Resolved" value="resolved" />
      </TabBar>
      <TicketFilters onApply={filterResults} onClear={() => setFilters({ priority: "", topic: "" })} filters={filters} />
    
      {isLoading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={SPINNER} alt="loading" className="w-6 h-6" />
        </div>
      ) : data&& (data as any).length > 0 ? (
        <>
          <TicketTableContainer 
            data={data as any} 
            handleSort={handleSort}
          />
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
    </div>
  );
}